import React, { useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { EntitlementDefsAutoComplete } from '@hcs/account-api';
import {
  ActionButtons,
  DeleteIcon,
  IconButton,
  Menu,
  TextButton,
} from '@hcs/design-system';
import { FormFieldsDate, InputLayout } from '@hcs/forms';
import { useActiveState } from '@hcs/hooks';
import {
  EntitlementapiEntitlementDefRespInt,
  EntitlementapiInputEntitlementRequestInt,
} from '@hcs/types';

import { useAddEntitlementToOrg } from '../../hooks/useAddEntitlementToOrg';

import styles from './AddEntitlementToOrg.module.css';

const FORM_SCHEMA = yup.object().shape({
  entitlementKeys: yup
    .array()
    .of(yup.string())
    .min(1)
    .required('Please select at least one entitlement'),
  active_on: yup.string().required('Active On is required'),
  active_until: yup.string(),
});

interface Props {
  orgId: number;
  actionButtonPortalIdRender?: string;
  onCancel: VoidFunction;
  onSuccess: VoidFunction;
}
const dataHcName = 'add-entitlement-to-org-form';
export const AddEntitlementToOrgForm = ({
  orgId,
  actionButtonPortalIdRender,
  onCancel,
  onSuccess,
}: Props) => {
  const { mutate: addEntitlementToOrg } = useAddEntitlementToOrg({ onSuccess });
  const {
    active: addAnotherActive,
    handleClose: addAnotherHide,
    handleOpen: addAnotherShow,
  } = useActiveState();
  const [selectedEntitlementDefs, setSelectedEntitlementDef] = useState<
    EntitlementapiEntitlementDefRespInt[]
  >([]);
  const form = useForm<
    Omit<EntitlementapiInputEntitlementRequestInt, 'entitlement_key'> & {
      entitlementKeys: string[];
    }
  >({
    resolver: yupResolver(FORM_SCHEMA),
  });
  return (
    <FormProvider {...form}>
      <form className={styles.Form}>
        <InputLayout label="Active On" required>
          <FormFieldsDate name="active_on" />
        </InputLayout>
        <InputLayout label="Active Until" required={false}>
          <FormFieldsDate name="active_until" />
        </InputLayout>
        <InputLayout label="Entitlements" required>
          <Controller
            name="entitlementKeys"
            control={form.control}
            render={({ field }) => {
              return (
                <>
                  <Menu
                    menuItems={selectedEntitlementDefs.map((entitlementDef) => {
                      return {
                        dataHcName: `add-entitlement-option-${entitlementDef.key}`,
                        label: entitlementDef.display_name,
                        description: entitlementDef.description,
                        contentRight: (
                          <IconButton
                            icon={<DeleteIcon />}
                            dataHcName={`${dataHcName}-chagne-entitlementdef`}
                            onClick={() => {
                              const newVal = selectedEntitlementDefs.filter(
                                (v) => v.key !== entitlementDef.key,
                              );
                              setSelectedEntitlementDef(newVal);
                              field.onChange(newVal.map((e) => e.key));
                              addAnotherHide();
                            }}
                          />
                        ),
                      };
                    })}
                  />
                  {!field.value || !field.value.length || addAnotherActive ? (
                    <EntitlementDefsAutoComplete
                      value={undefined}
                      onSelect={(entitlementDef) => {
                        if (!entitlementDef) return;
                        const newVal = [
                          ...selectedEntitlementDefs,
                          entitlementDef,
                        ];
                        field.onChange(newVal.map((e) => e.key));
                        if (entitlementDef) {
                          setSelectedEntitlementDef(newVal);
                          addAnotherHide();
                        }
                        form.clearErrors('entitlementKeys');
                      }}
                    />
                  ) : (
                    <TextButton
                      dataHcName={`${dataHcName}-add-another`}
                      onClick={addAnotherShow}
                    >
                      Add another entitlement
                    </TextButton>
                  )}
                </>
              );
            }}
          />
        </InputLayout>
        <ActionButtons
          dataHcName={`${dataHcName}-actions`}
          portalIdRender={actionButtonPortalIdRender}
          actions={[
            {
              label: 'Cancel',
              onClick: onCancel,
              dataHcName: `${dataHcName}-cancel`,
              secondary: true,
            },
            {
              label: 'Submit',
              type: 'submit',
              disabled: !form.formState.isValid,
              onClick: () => {
                const { entitlementKeys, ...formData } = form.getValues();
                const active_on = new Date(
                  formData.active_on || '',
                ).toISOString();
                const active_until = formData.active_until
                  ? new Date(formData.active_until).toISOString()
                  : undefined;
                entitlementKeys.forEach((entitlement_key) => {
                  addEntitlementToOrg({
                    orgId,
                    ...formData,
                    entitlement_key,
                    active_on,
                    active_until,
                  });
                });
              },
              dataHcName: `${dataHcName}-cancel`,
              primary: true,
            },
          ]}
        />
      </form>
    </FormProvider>
  );
};
